import {
  getNormalizedPageSettingOverrides,
  HiddenSlidePageSettingOverride,
} from '../hiddenSlides';
import { ShareType } from '@alucio/aws-beacon-amplify/src/models';

export type SharePageSetting = HiddenSlidePageSettingOverride;

type SharePageSettingsState = {
  user?: {
    cognitoUser?: {
      attributes?: Record<string, string | boolean | undefined>,
    },
  },
  userDocumentVersionSettings: {
    records: Array<{
      createdBy?: string | null,
      customDeckId?: string | null,
      documentVersionId?: string | null,
      pageSettings?: SharePageSetting[] | null,
      updatedAt: string,
    }>,
  },
};

export const getNormalizedSharePageSettings = (
  pageSettings?: SharePageSetting[] | null,
): SharePageSetting[] => getNormalizedPageSettingOverrides(pageSettings ?? []);

export const matchesSharePageSettings = (
  a?: SharePageSetting[] | null,
  b?: SharePageSetting[] | null,
): boolean => {
  const normalizedA = getNormalizedSharePageSettings(a);
  const normalizedB = getNormalizedSharePageSettings(b);

  return normalizedA.length === normalizedB.length &&
    normalizedA.every((aVal, index) =>
      aVal.pageId === normalizedB[index]?.pageId &&
      aVal.hidden === normalizedB[index]?.hidden);
};

export const getCurrentSharePageSettings = (
  state: SharePageSettingsState,
  type: ShareType,
  contentId: string,
): SharePageSetting[] | undefined => {
  if (type !== ShareType.DOCUMENT_VERSION && type !== ShareType.CUSTOM_DECK) {
    return undefined;
  }

  const cognitoUserId = state.user?.cognitoUser?.attributes?.['custom:user_id'];
  const currentUserId = typeof cognitoUserId === 'string' ? cognitoUserId : undefined;
  if (!currentUserId) {
    return undefined;
  }

  // A share stores the sharer's current preferences as a snapshot for public access.
  const settings = [...state.userDocumentVersionSettings.records]
    .filter(setting => setting.createdBy === currentUserId)
    .sort((a, b) => new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime())
    .reduce<SharePageSettingsState['userDocumentVersionSettings']['records'][number] | undefined>(
      (latestSettings, setting) => {
        if (type === ShareType.DOCUMENT_VERSION &&
          setting.documentVersionId === contentId &&
          !setting.customDeckId
        ) {
          return setting;
        }

        if (type === ShareType.CUSTOM_DECK && setting.customDeckId === contentId) {
          return setting;
        }

        return latestSettings;
      },
      undefined,
    );

  return getNormalizedSharePageSettings(settings?.pageSettings);
};
